/**
 * Loads Swiper on demand (FB-1541). Swiper used to be a parser-blocking
 * stylesheet + script from cdn.jsdelivr.net on every page, the last
 * render-blocking resource to finish in the CWV audit (FB-1504). Its only live
 * consumers are the cart's product pickers (`assets/custom-cart-components.js`)
 * and the bundle builder (`assets/product-bundle-builder.js`), so each now
 * calls `await window.FarmacyLoadSwiper()` right before `new window.Swiper()`.
 *
 * The self-hosted asset URLs come from `window.FarmacySwiperAssets` ({ css, js }),
 * set in an inline config block in `layout/theme.liquid` (and in
 * `sections/bundle-builder.liquid`, for a store with the custom cart off).
 *
 * Injects each asset at most once, and skips one the page already references —
 * the bundle builder section keeps its own Swiper stylesheet so its first paint
 * is unchanged. Resolves with `window.Swiper` once the script has run and the
 * stylesheet has settled, so Swiper never measures unstyled slides. A failed
 * script load rejects and is retried on the next call.
 *
 * Can be included more than once (the bundle builder section includes it too);
 * the first copy to run wins.
 */
(() => {
  if (window.FarmacyLoadSwiper) {
    return;
  }

  let pending = null;

  const resolveUrl = (url) => new URL(url, document.baseURI).href;

  const findExisting = (selector, attribute, url) => {
    const target = resolveUrl(url);
    return [...document.querySelectorAll(selector)].find(
      (el) => resolveUrl(el.getAttribute(attribute)) === target,
    );
  };

  const loadStylesheet = (url) => {
    if (!url || findExisting('link[rel="stylesheet"][href]', 'href', url)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      // A missing stylesheet must not keep the carousel from working at all.
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(link);
    });
  };

  const loadScript = (url) => {
    if (window.Swiper) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const existing = findExisting('script[src]', 'src', url);
      const script = existing || document.createElement('script');
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener(
        'error',
        () => {
          // Drop the failed tag so a retry injects a fresh one instead of
          // waiting on this one forever.
          script.remove();
          reject(new Error(`Swiper failed to load from ${url}`));
        },
        { once: true },
      );
      if (!existing) {
        script.src = url;
        document.head.appendChild(script);
      }
    });
  };

  window.FarmacyLoadSwiper = () => {
    if (window.Swiper) {
      return Promise.resolve(window.Swiper);
    }
    if (pending) {
      return pending;
    }

    const assets = window.FarmacySwiperAssets || {};
    if (!assets.js) {
      return Promise.reject(
        new Error('window.FarmacySwiperAssets.js is not set'),
      );
    }

    pending = Promise.all([loadStylesheet(assets.css), loadScript(assets.js)])
      .then(() => {
        if (!window.Swiper) {
          throw new Error('Swiper loaded but window.Swiper is undefined');
        }
        return window.Swiper;
      })
      .catch((error) => {
        pending = null;
        throw error;
      });

    return pending;
  };
})();
